import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	input,
	viewChild,
} from '@angular/core';
import {
	Field,
	PdftronComponent,
} from '@components/pdf-modal/pdftron/pdftron.component';
import { base64ToString, isSet, stringToBase64 } from '@core/util/value';
import { FormInstance } from '@models/global-forms';
import { MatterFormsSource } from '@models/matter-forms';
import { dispatch, select } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import {
	FormDataActions,
	MatterFormV2DataState,
} from '@state/matter/form-data-v2';

@Component({
	selector: 'app-matter-form-instance-viewer',
	templateUrl: './matter-form-instance-viewer.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class FormInstanceViewerComponent {
	Create = dispatch(FormDataActions.Create);
	SetField = dispatch(FormDataActions.SetField);
	pdfViewer = viewChild('pdfViewer', { read: PdftronComponent });
	form = input<FormInstance>(null);

	uploadedDocGoogleSrcUrl = input<string>(null, { alias: 'googleSrcUrl' });

	formData = computed(
		() => (this.uploadedDocGoogleSrcUrl() ? null : this.loadFile()), // uploadedDocs don't have tags
	);

	formName = computed(
		() => (this.uploadedDocGoogleSrcUrl ? null : this.form()?.name), // uploadedDocs don't have tags
	);

	googleSrcUrl = computed(() => {
		const loadedForm = this.businessMatterForm();
		const form = this.form();
		return (
			this.uploadedDocGoogleSrcUrl() ??
			(loadedForm?.barcodeUrl || loadedForm?.url) ??
			form?.barcodeStorageRefPath ??
			form?.storageRefPath
		);
	});

	private matterId_ = select(SelectedMatter.id);
	private allBusinessMatterFormData = select(
		MatterFormV2DataState.getAllFormData,
	);

	private readonly _formDataId = computed(() => {
		const form = this.form();
		const formId = form?.context?.id ?? form?.id;

		return this.uploadedDocGoogleSrcUrl()
			? null
			: formId == null
				? null
				: encodeURIComponent(formId); // uploadedDocs don't have tags
	});

	private businessMatterForm = computed(
		() =>
			this.uploadedDocGoogleSrcUrl()
				? null
				: this.allBusinessMatterFormData().find(
						(_form) => _form.id === this._formDataId(),
					), // uploadedDocs don't have tags
	);

	constructor() {
		effect(() => {
			const loadedForm = this.businessMatterForm();
			const form = this.form();
			if (!(form && loadedForm && (loadedForm.barcodeUrl || loadedForm.url))) {
				this.Create({
					id: this._formDataId(),
					matterId: this.matterId_(),
					fields: {},
					sourceId: form?.id,
					source: MatterFormsSource.GLOBAL,
					url: form?.storageRefPath,
					barcodeUrl: form?.barcodeStorageRefPath || null,
				});
			}
		});
	}

	loadFile() {
		const form = this.form();
		const loadedForm = this.businessMatterForm();

		// Resolves the tag data
		const data = Object.values(form?.fields ?? {}).reduce((prev, value) => {
			const val = value.tagValue as unknown;

			if (val) {
				prev[value.fieldName] = val;
			}
			return prev;
		}, {});

		// Gets the overridden values from formDataV2
		Object.entries(loadedForm?.fields ?? {}).forEach(([key, value]) => {
			if (isSet(value.override)) {
				const base64Key = base64ToString(key);
				data[base64Key] = value.override;
			}
		});

		return data;
	}

	fieldChanged(event: Field): void {
		// Compare values only and disregard types because some forms use number type values but form field values are stored in Firestore as strings
		// eslint-disable-next-line eqeqeq
		if (this.formData()?.[event.field] == event.value) {
			return;
		}

		const id = this._formDataId();
		if (!id) {
			console.error('Form data ID is null, cannot set field');
			return;
		}

		const base64Key = stringToBase64(event.field);

		this.SetField({
			id,
			field: base64Key,
			value: event.value,
		});
	}
}
